import Vue from 'vue';
import router from '@/router';
import { Dictionary } from 'vue-router/types/router';

export type UrlParam = { [key: string]: string | string[] };

const observableParams = Vue.observable({});

// Resets observable facets for every route-change
router.afterEach(updateObservableParams);

function updateObservableParams() {
    // Clean
    Object.keys(observableParams).forEach(param => Vue.delete(observableParams, param));

    const query = { ...router.currentRoute.query };

    // Refill
    Object.keys(query).forEach(facetGroup => {
        Vue.set(observableParams, facetGroup, query[facetGroup]);
    });
}

class UrlService {
    get params(): UrlParam {
        return observableParams;
    }

    public setQueryParam(param: string, value?: any | any[]) {
        const query = { ...router.currentRoute.query };

        if (value == null || value === undefined) {
            this.removeQueryParam(param);
            return;
        }

        if (typeof value === 'string' && value.length === 0) {
            this.removeQueryParam(param);
            return;
        }

        if (!query[param]) {
            query[param] = value;
        } else if (value instanceof Array) {
            if (query[param] instanceof Array) {
                const queryParamArr = query[param] as string[];

                for (const val of value) {
                    if (!queryParamArr.includes(val)) {
                        queryParamArr.push(val);
                    }
                }

                query[param] = queryParamArr;
            } else {
                const queryParamArr: string[] = [];
                for (const val of value) {
                    if (query[param] === val) {
                        continue;
                    }

                    queryParamArr.push(val);
                }
                query[param] = [query[param] as string, ...queryParamArr];
            }
        } else {
            query[param] = value;
        }

        this.replaceQuery(query);
    }

    public removeQueryParam(param: string, value?: string | string[]) {
        const query = { ...router.currentRoute.query };

        if (value instanceof Array) {
            if (query[param] instanceof Array) {
                const queryParamArr = query[param] as string[];

                for (const val of value) {
                    queryParamArr.splice(queryParamArr.indexOf(val), 1);
                }

                if (queryParamArr.length === 0) {
                    delete query[param];
                } else {
                    query[param] = queryParamArr;
                }
            } else {
                if (query[param] === value) {
                    delete query[param];
                }
            }
        } else {
            delete query[param];
        }

        this.replaceQuery(query);
    }

    public setPage(page: number) {
        this.setQueryParam('page', page);
    }

    public getPage() {
        const value = parseInt(this.getQueryParam('page') || '', 10);
        return !value || Number.isNaN(value) ? 1 : value;
    }

    public hasQueryParam(param: string, ...args: string[]): boolean {
        const query = { ...router.currentRoute.query };

        if (!query[param]) {
            return false;
        }

        if (query[param] instanceof Array) {
            return (query[param] as string[]).filter(x => args.indexOf(x) > -1).length > 0;
        } else {
            return args.indexOf(query[param] as string) > -1;
        }
    }

    public replaceQuery(query: Dictionary<string | (string | null)[] | null | undefined>) {
        router.replace({ query: undefined }).catch(() => {}); // have to clear it for some fucking reason
        router.replace({ query }).catch(() => {});
    }

    public getQueryParam<T extends string | string[]>(param: string): T | undefined {
        return (router.currentRoute.query[param] as unknown) as T;
    }

    public getBaseUrl() {
        return location.protocol + '//' + location.host + location.pathname;
    }
}

export default new UrlService();
